import React, { useState, useEffect, useCallback } from 'react';
import { fetchPayouts, recordPayout } from '../api';
import PayoutModal from './PayoutModal';
import ManuLeads from './ManuLeads';
import ManufacturerAcceptances from './ManufacturerAcceptances';
import './Dashboard.css';

const PAYOUT_METHOD_LABELS = {
  wireTransfer: 'Wire Transfer',
  paypal: 'PayPal',
  wisePay: 'Wise',
  ria: 'RIA Money Transfer',
  moneyGram: 'MoneyGram',
  remitly: 'Remitly'
};

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'completed', label: 'Completed' }
];

const Dashboard = ({ session, onLogout, activeView = 'payouts', onPendingLeadsCountChange }) => {
  const [payouts, setPayouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const accessToken = session?.access_token;

  const loadPayouts = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchPayouts(accessToken, statusFilter);
      setPayouts(data);
    } catch (err) {
      if (err.message === 'FORBIDDEN') {
        onLogout();
        return;
      }
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, statusFilter, onLogout]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  const handleRecord = async (payoutId, formData) => {
    try {
      await recordPayout(accessToken, { payoutId, ...formData });
      setSelectedPayout(null);
      setSuccessMsg('Payout recorded successfully! Manufacturer has been notified.');
      setTimeout(() => setSuccessMsg(''), 4000);
      loadPayouts();
    } catch (err) {
      throw err;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getPayoutDetail = (info) => {
    if (!info?.payment_details) return '—';
    const d = info.payment_details;
    if (d.email) return d.email;
    if (d.fullName) return d.fullName;
    if (d.accountName) return d.accountName;
    if (d.recipientName) return d.recipientName;
    if (d.accountNumber) return `****${d.accountNumber.slice(-4)}`;
    return '—';
  };

  const getStatusPill = (status) => {
    switch (status) {
      case 'completed': return <span className="admin-pill admin-pill-completed">Completed</span>;
      case 'sent': return <span className="admin-pill admin-pill-sent">Sent</span>;
      case 'failed': return <span className="admin-pill admin-pill-failed">Failed</span>;
      default: return <span className="admin-pill admin-pill-pending">Pending</span>;
    }
  };

  const handleExport = () => {
    if (payouts.length === 0) return;
    const headers = 'Date,Manufacturer,Company,Quote,Type,Client,Payout Type,Amount,Payout Method,Status,Reference,Tchpack Ref,Sent Date,Completed Date';
    const rows = payouts.map(p => {
      const info = p.manufacturer_payout_info;
      const manuName = info?.full_name || p.profiles?.full_name || 'Unknown';
      const company = info?.company_name || '';
      const clientName = p.client?.full_name || '';
      // Historical truth first (the method this payout was actually sent via);
      // for pending rows that haven't been recorded yet, fall back to the
      // manufacturer's current preferred method as a hint.
      const rawMethod = p.payout_method || info?.payout_method || '';
      const method = rawMethod ? (PAYOUT_METHOD_LABELS[rawMethod] || rawMethod) : '';
      const payoutType = p.payout_number === 1 ? '70%' : '30%';
      const date = p.created_at ? new Date(p.created_at).toISOString().split('T')[0] : '';
      const sentDate = p.sent_at ? new Date(p.sent_at).toISOString().split('T')[0] : '';
      const completedDate = p.completed_at ? new Date(p.completed_at).toISOString().split('T')[0] : '';
      return `${date},"${manuName}","${company}","${p.quote_number || ''}",${p.quote_type || ''},"${clientName}",${payoutType},${p.amount},${method},${p.status},${p.reference_number || ''},${p.payout_ref || ''},${sentDate},${completedDate}`;
    });
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tchpack-payouts-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const filteredPayouts = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return payouts;
    return payouts.filter((p) => {
      const info = p.manufacturer_payout_info;
      const haystack = [
        p.reference_number,
        p.payout_ref,
        info?.full_name,
        info?.company_name,
        p.profiles?.full_name,
        p.client?.full_name,
        p.quote_number
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  })();

  const pendingCount = payouts.filter(p => p.status === 'pending').length;
  const totalPending = payouts
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + Number(p.amount), 0);
  const totalAll = payouts.reduce((sum, p) => sum + Number(p.amount), 0);

  return (
    <>
      {/* Success Banner */}
      {successMsg && activeView === 'payouts' && (
        <div className="admin-success-banner">
          {successMsg}
        </div>
      )}

      {/* Content */}
      <main className="admin-main">
        {activeView === 'leads' && (
          <ManuLeads
            session={session}
            onLogout={onLogout}
            onPendingCountChange={onPendingLeadsCountChange}
          />
        )}

        {activeView === 'compliance' && (
          <ManufacturerAcceptances accessToken={accessToken} onForbidden={onLogout} />
        )}

        {activeView === 'payouts' && (
        <>
        {/* Summary Bar */}
        <div className="admin-summary">
          <div className="admin-summary-card">
            <span className="admin-summary-label">Pending Action</span>
            <span className="admin-summary-value">{pendingCount}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">Pending Amount</span>
            <span className="admin-summary-value">${totalPending.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">Total Payouts</span>
            <span className="admin-summary-value">{payouts.length}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">Total Amount</span>
            <span className="admin-summary-value">${totalAll.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="admin-error">{error}</div>
        )}

        {/* Payouts Table */}
        <div className="admin-table-wrapper">
          <div className="admin-table-header-bar">
            <h2>Payouts</h2>
            <div className="admin-table-header-right">
              <div className="admin-search-box">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="Search reference, manufacturer, client..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="admin-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                    ×
                  </button>
                )}
              </div>
              <button className="admin-refresh-btn" onClick={loadPayouts} disabled={isLoading}>
                Refresh
              </button>
              <button className="admin-export-btn" onClick={handleExport} disabled={payouts.length === 0}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
              <span className="admin-table-count">{filteredPayouts.length} items</span>
            </div>
          </div>

          {/* Status Tabs */}
          <div className="admin-tabs">
            {STATUS_TABS.map(tab => (
              <button
                key={tab.value}
                className={`admin-tab ${statusFilter === tab.value ? 'admin-tab-active' : ''}`}
                onClick={() => setStatusFilter(tab.value)}
              >
                {tab.label}
                {tab.value === 'pending' && pendingCount > 0 && (
                  <span className="admin-tab-badge">{pendingCount}</span>
                )}
              </button>
            ))}
          </div>

          {/* Loading */}
          {isLoading ? (
            <div className="admin-loading">Loading payouts...</div>
          ) : filteredPayouts.length === 0 ? (
            <div className="admin-empty">
              <h3>{searchQuery ? 'No matching payouts' : statusFilter ? `No ${statusFilter} payouts` : 'No payouts yet'}</h3>
              <p>{searchQuery ? 'Try a different reference number, manufacturer, or client name.' : statusFilter === 'pending' ? 'All caught up! No pending payouts to process.' : 'Payouts will appear here as orders come in.'}</p>
            </div>
          ) : (
            <>
              <div className="admin-table-head">
                <span className="admin-col admin-col-manu">Manufacturer</span>
                <span className="admin-col admin-col-quote">Quote</span>
                <span className="admin-col admin-col-client">Client</span>
                <span className="admin-col admin-col-type">Type</span>
                <span className="admin-col admin-col-amount">Amount</span>
                <span className="admin-col admin-col-method">Payout Method</span>
                <span className="admin-col admin-col-status">Status</span>
                <span className="admin-col admin-col-date">Date</span>
                <span className="admin-col admin-col-action">Action</span>
              </div>

              {filteredPayouts.map((p) => {
                const methodInfo = p.manufacturer_payout_info;
                // Historical method from the payout row itself; for pending
                // rows (not yet recorded) fall back to the manufacturer's
                // current preferred method as a hint.
                const rawMethod = p.payout_method || methodInfo?.payout_method || '';
                const methodLabel = rawMethod
                  ? (PAYOUT_METHOD_LABELS[rawMethod] || rawMethod)
                  : '—';
                const payoutLabel = p.payout_number === 1 ? '70%' : '30%';

                return (
                  <div key={p.id} className={`admin-table-row ${p.status === 'pending' ? 'admin-row-pending' : ''}`}>
                    <span className="admin-col admin-col-manu">
                      <span className="admin-manu-name">{methodInfo?.full_name || p.profiles?.full_name || 'Unknown'}</span>
                      {methodInfo?.company_name && (
                        <span className="admin-manu-company">{methodInfo.company_name}</span>
                      )}
                    </span>
                    <span className="admin-col admin-col-quote">
                      <span className="admin-quote-num">{p.quote_number || '—'}</span>
                      <span className="admin-quote-type">{p.quote_type === 'custom' ? 'Custom' : 'Project'}</span>
                    </span>
                    <span className="admin-col admin-col-client">
                      {p.client?.full_name || '—'}
                    </span>
                    <span className="admin-col admin-col-type">
                      <span className={`admin-type-badge admin-type-${payoutLabel}`}>{payoutLabel}</span>
                    </span>
                    <span className="admin-col admin-col-amount admin-amount-value">
                      ${Number(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="admin-col admin-col-method">
                      <span className="admin-method-name">{methodLabel}</span>
                      <span className="admin-method-detail">{getPayoutDetail(methodInfo)}</span>
                    </span>
                    <span className="admin-col admin-col-status">
                      {getStatusPill(p.status)}
                    </span>
                    <span className="admin-col admin-col-date">
                      {formatDate(p.status === 'completed' ? p.completed_at : p.status === 'sent' ? p.sent_at : p.created_at)}
                    </span>
                    <span className="admin-col admin-col-action">
                      {p.status === 'pending' ? (
                        <button
                          className="admin-record-btn"
                          onClick={() => setSelectedPayout(p)}
                        >
                          Record Payout
                        </button>
                      ) : (
                        <span className="admin-ref-num">{p.reference_number || '—'}</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>
        </>
        )}
      </main>

      {/* Record Payout Modal */}
      {selectedPayout && (
        <PayoutModal
          payout={selectedPayout}
          payoutInfo={selectedPayout.manufacturer_payout_info}
          onClose={() => setSelectedPayout(null)}
          onSubmit={handleRecord}
        />
      )}
    </>
  );
};

export default Dashboard;
